import React from 'react';
import { Navigate } from 'react-router-dom';
import { useLegacyAppSession } from './contexts/LegacyAppSessionContext';

type PrivateRouteProps = {
  requiredAction?: string | string[]; //one of the listed actions is required for the route
  requireSuperUser?: boolean;
  bypassAuthorization?: boolean;
  redirectTo?: string;
  children: React.ReactNode;
};

 //generic permission-gate wrapper for routes.
 //wrap any <Route> element to enforce authentication and
 //action-level authorization via JWT claims.
 /*
 * Usage:
 *   <Route
 *     path="/internal/*"
 *     element={
 *       <PrivateRoute requiredAction="admin:read">
 *         <InternalPage />
 *       </PrivateRoute>
 *     }
 *   />
 */
export default function PrivateRoute({
  requiredAction,
  requireSuperUser,
  bypassAuthorization = false,
  redirectTo = '/internal',
  children,
}: PrivateRouteProps) {
  const {
    currentUser,
    hasStoredToken,
    isAuthenticated,
    isSessionReady,
  } = useLegacyAppSession();

  if (!isSessionReady && hasStoredToken) {
    return null;
  }

  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isSuperUser = currentUser.isSuperUser === true;

  if (requireSuperUser && !isSuperUser) {
    return <Navigate to={redirectTo} replace />;
  }

  const actions = Array.from(new Set([
    ...(currentUser.roleActions || []),
    ...(currentUser.permissions?.actions || []),
    ...(currentUser.internalRoleActions || []),
  ].filter(Boolean)));
  const requiredActions = (Array.isArray(requiredAction) ? requiredAction : [requiredAction]).filter(Boolean) as string[];
  const hasRequiredAction = requiredActions.some((action) => actions.includes(action));

  if (requiredActions.length && !bypassAuthorization && !hasRequiredAction) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
